"use client";

import Image from "next/image";
import { useEffect, useState } from "react";

/**
 * Rotation slide layers for the dark-funnel hero.
 *
 * The raw CSS `background-image` version of these slides downloaded the
 * full-size JPGs (~360-410KB each) at High priority during initial load,
 * saturating slow connections and pushing LCP past 8s in Lighthouse. The
 * slides are invisible until 5.1s/11.1s into the CSS rotation, so nothing
 * above the fold needs them at load time.
 *
 * This component keeps the exact 3-child DOM shape the
 * `.roma-funnel-hero-slides > div:nth-child(n)` animation CSS expects
 * (slide 1 stays an empty div over the prioritized next/image base layer),
 * but mounts slides 2-3 as optimized next/image renders only after
 * the LCP window. `window` load is too early: Lighthouse still records
 * those fetches against lab LCP. Wait 4s after load so slide 1 stays
 * the only hero image on the critical path.
 */
const ROTATION_SLIDE_DELAY_MS = 4000;

export function RomaHeroRotationSlides({
  images,
}: {
  images: readonly string[];
}) {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let timeoutId = 0;
    const arm = () => {
      timeoutId = window.setTimeout(() => setReady(true), ROTATION_SLIDE_DELAY_MS);
    };

    if (document.readyState === "complete") {
      arm();
    } else {
      window.addEventListener("load", arm, { once: true });
    }

    return () => {
      window.removeEventListener("load", arm);
      window.clearTimeout(timeoutId);
    };
  }, []);

  return (
    <div className="roma-funnel-hero-slides absolute inset-0">
      {images.map((src, index) => (
        <div key={src} className="absolute inset-0 overflow-hidden">
          {index > 0 && ready ? (
            <Image
              src={src}
              alt=""
              fill
              sizes="100vw"
              quality={70}
              className="object-cover object-center"
            />
          ) : null}
        </div>
      ))}
    </div>
  );
}
